import { memo } from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";

import { TerminationReasons, type TerminationAnecdotes } from "graphql_globals";
import {
  TERM_REASONS_MESSAGES,
  TERM_ANECDOTES_MESSAGES,
  getReasonForAnecdote,
} from "constants/meeting_termination";
import { Paragraph } from "common/core/typography";

import { NotaryItemWrapper } from "../../../notary_item_wrapper";

type Props = {
  meeting: {
    failureAnecdote: string | null;
    terminationReasons: (TerminationReasons | null)[] | null;
    terminationAnecdotes: (TerminationAnecdotes | null)[] | null;
  };
};

function renderReason(
  intl: IntlShape,
  reason: TerminationReasons | null | undefined,
  allTerminationAnecdotes: Props["meeting"]["terminationAnecdotes"],
) {
  if (!reason) {
    return null;
  }
  return (
    <div key={reason}>
      <Paragraph>{intl.formatMessage(TERM_REASONS_MESSAGES[reason])}</Paragraph>
      {allTerminationAnecdotes
        ?.filter((anecdote) => getReasonForAnecdote(anecdote!) === reason)
        .map((anecdote) => intl.formatMessage(TERM_ANECDOTES_MESSAGES[anecdote!]))
        .join(", ")}
    </div>
  );
}

function Info({ meeting }: Props) {
  const { terminationReasons, terminationAnecdotes, failureAnecdote } = meeting;
  const intl = useIntl();
  return (
    <NotaryItemWrapper
      term={<FormattedMessage id="1afffc4e-5c88-49d7-8cdf-0e9fa858b436" defaultMessage="Issues" />}
      definition={
        <>
          {terminationReasons && (
            <>
              {/* Put MISC category at bottom of list so it is next to details */}
              {terminationReasons
                .filter((reason) => reason !== TerminationReasons.MISC)
                .map((reason) => renderReason(intl, reason, terminationAnecdotes))}
              {renderReason(
                intl,
                terminationReasons.find((reason) => reason === TerminationReasons.MISC),
                terminationAnecdotes,
              )}
            </>
          )}
          {failureAnecdote && (
            <div>
              <Paragraph>
                <FormattedMessage
                  id="e528e565-4566-40bd-bc48-9dddb026d608"
                  defaultMessage="Details"
                />
              </Paragraph>
              {failureAnecdote}
            </div>
          )}
        </>
      }
    />
  );
}

export default memo(Info);
